import { useState, useEffect, useMemo } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog.jsx'
import { useDashboardContext } from '@/context-state/DashboardContext.jsx'
import { IntegrationProviders } from '@/consts/index.js'
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { TypographySmall, TypographyXSmallMuted } from '@/components/ui/typography.jsx'

/**
 * A reusable component for selecting ad accounts via a dialog
 */

export const AdAccountsDialog = ({ isDialogOpen, setIsDialogOpen, chosenAdAccountsIds = [], title = "Choose Ad Accounts", description = "Select ad accounts to add", onConfirm }) => {
    // Get Meta integration data from context
    const { integrationsContext } = useDashboardContext()
    const { integrations } = integrationsContext
    const metaIntegration = integrations.find(integration => integration.provider === IntegrationProviders.META)
    
    // State for ad account selection
    const [selectedAccounts, setSelectedAccounts] = useState(chosenAdAccountsIds)

    const [searchTerm, setSearchTerm] = useState("")
    
    // Filter accounts based on search term
    const filteredAccounts = useMemo(() => 
        (metaIntegration?.adAccounts || []).filter(
            account => account.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                      account.id.toString().includes(searchTerm)
        ),
        [metaIntegration?.adAccounts, searchTerm]
    )
    
    // Determine if all accounts are selected
    const selectAll = useMemo(
        () => filteredAccounts.length > 0 && selectedAccounts.length === filteredAccounts.length,
        [selectedAccounts, filteredAccounts]
    )
    
    // Toggle selection of an account
    const handleAccountToggle = (accountId) => {
        setSelectedAccounts(prev =>
            prev.includes(accountId) ? prev.filter(id => id !== accountId) : [...prev, accountId]
        )
    }
    
    // Toggle selection of all accounts
    const handleSelectAllToggle = () => {
        if (selectAll) {
            setSelectedAccounts([])
        } else {
            setSelectedAccounts(filteredAccounts.map(account => account.id))
        }
    }
    
    // Handle search input change
    const handleSearch = (e) => {
        setSearchTerm(e.target.value)
    }
    
    // Handle confirmation button click
    const handleConfirm = () => {
        if (onConfirm) {
            onConfirm(selectedAccounts)
        }
        setIsDialogOpen(false)
    }
    
    // Initialize selected accounts from pre-selected accounts
    useEffect(() => {
        if (isDialogOpen) {
            setSelectedAccounts(chosenAdAccountsIds)
        }
    }, [isDialogOpen])

    return (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogContent className="min-w-[600px]">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>{description}</DialogDescription>
                </DialogHeader>
                
                <div className="flex flex-col gap-4 py-4">
                    <Input 
                        placeholder="Search accounts..." 
                        value={searchTerm} 
                        onChange={handleSearch} 
                        className="flex-grow" 
                    />
                    
                    <div className="flex items-center space-x-2 border-b pb-4">
                        <Checkbox 
                            id="select-all" 
                            checked={selectedAccounts.length === filteredAccounts.length} 
                            onCheckedChange={handleSelectAllToggle} 
                        />
                        <div className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            Select All {filteredAccounts.length ? `(${filteredAccounts.length})` : ""}
                        </div>
                    </div>
                    
                    <div className="flex flex-col gap-3 h-[400px] overflow-scroll py-2">
                        {
                            filteredAccounts.map((account) => {
                                const isSelected = selectedAccounts.includes(account.id)
                                return (
                                    <div key={account.id} className="flex items-center space-x-2">
                                        <Checkbox
                                            id={`account-${account.id}`}
                                            checked={isSelected}
                                            onCheckedChange={() => handleAccountToggle(account.id)}
                                        />
                                        <div className="flex flex-col gap-1">
                                            <TypographySmall>{account.name}</TypographySmall>
                                            <TypographyXSmallMuted>{account.id}</TypographyXSmallMuted>
                                        </div>
                                    </div>
                                )
                            })
                        }
                    </div>
                    
                    <Button 
                        onClick={handleConfirm} 
                        disabled={!selectedAccounts.length}
                    >
                        Save
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}
